import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { Popover, PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { PefAutofillEvent, PefIncrementMode } from '../pef-autofill';
import { PefSheet, resolvePefCell } from '../pef-schema.types';

/**
 * Popover form that builds a `row-progressive` autofill for a single row:
 * the user picks a start year, an interval and an increment (percentage or
 * fixed amount), and the resulting {@link PefAutofillEvent} is emitted.
 *
 * The host owns the trigger button and calls {@link open} to anchor and show
 * the popover; {@link activeRowId} lets the host highlight that trigger while
 * the popover is open.
 */
@Component({
  selector: 'lib-pef-progressive-fill',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, PopoverModule, InputNumberModule, SelectModule],
  templateUrl: './pef-progressive-fill.html',
  styleUrl: './pef-progressive-fill.css',
})
export class PefProgressiveFill {
  readonly sheet = input.required<PefSheet>();

  readonly apply = output<PefAutofillEvent>();

  private readonly menu = viewChild<Popover>('menu');

  /** Row whose popover is currently open (null when closed). */
  readonly activeRowId = signal<string | null>(null);

  protected readonly startColumnId = signal<string | null>(null);
  protected readonly interval = signal<number | null>(1);
  protected readonly increment = signal<number | null>(null);
  protected readonly mode = signal<PefIncrementMode>('percentage');

  /** Input (year) columns of the active row. */
  protected readonly rowColumns = computed(() => {
    const rowId = this.activeRowId();
    if (!rowId) return [];
    const sheet = this.sheet();
    const row = sheet.rows.find((r) => r.id === rowId);
    if (!row) return [];
    return sheet.columns
      .filter((c) => resolvePefCell(sheet, row, c.id).type === 'input')
      .map((c) => ({ label: c.label, value: c.id }));
  });

  protected readonly modeOptions: Array<{
    label: string;
    value: PefIncrementMode;
  }> = [
    { label: '% (percentuale)', value: 'percentage' },
    { label: 'Importo fisso', value: 'absolute' },
  ];

  /** Opens the popover for `rowId`, anchored to the triggering `event`. */
  open(rowId: string, event: Event): void {
    this.activeRowId.set(rowId);
    this.interval.set(1);
    this.increment.set(null);
    this.mode.set('percentage');
    // Default to the row's first year column.
    this.startColumnId.set(this.rowColumns()[0]?.value ?? null);
    this.menu()?.toggle(event);
  }

  protected onApply(): void {
    const rowId = this.activeRowId();
    const increment = this.increment();
    if (!rowId || increment === null) return;
    this.menu()?.hide();
    this.apply.emit({
      type: 'row-progressive',
      sheetId: this.sheet().id,
      sourceColumnId: this.sheet().columns[0]?.id ?? '',
      rowId,
      startColumnId: this.startColumnId() ?? undefined,
      interval: this.interval() ?? 1,
      increment,
      incrementMode: this.mode(),
    });
  }
}
